import { yupResolver } from '@hookform/resolvers/yup';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import { VIEWPORT_HEIGHT } from 'coreOld/constants';
import { useFocusFunctionality } from 'coreOld/hooks';
import { cloneArrayOfObjects } from 'coreOld/utils';
import { OTHER_DISCIPLINE } from 'corporate/constants';
import { IDiscipline } from 'corporate/types';
import { useGetJobAttachments, useJobActions } from 'jobsOld/hooks';
import { IJob } from 'jobsOld/types';
import { useGetSupportRequest } from 'support/hooks';
import { mapRequestToRequestJobs, mapUpsertRequestJobs } from 'support/mappers';
import { supportRequestJobListPath, supportRequestListPath } from 'support/routes/routePaths';
import { IRequestJobs } from 'support/types';

import { requestJobsDetailsSchema } from './requestJobsDetailsSchema';
import RequestJobsDetailsView from './RequestJobsDetailsView';

interface LocationState {
  state: { id: number };
}

interface IProps {
  requestId?: number;
}

const getExistingDisciplines = (jobs: { [jobId: number]: IJob }) => {
  const existingDisciplines = {};

  for (const jobId in jobs) {
    const { disciplineId, disciplineName } = jobs[jobId];

    existingDisciplines[disciplineId ?? OTHER_DISCIPLINE] = disciplineName;
  }

  return existingDisciplines;
};

const RequestJobsDetails = ({ requestId }: IProps) => {
  const [id, setId] = useState<number>(requestId);
  const [editMode] = useState<boolean>(!!requestId);

  const navigate = useNavigate();

  const { state } = useLocation() as LocationState;
  const { record, isLoading, isFetching, isError, statusCode } = useGetSupportRequest({
    id,
  });

  const {
    records: attachments,
    isLoading: isJobAttachmentsLoading,
    isError: isJobAttachmentsError,
    statusCode: jobAttachmentsStatusCode,
  } = useGetJobAttachments(
    {
      bomId: Object.keys(record?.bomsMap || []).map(Number),
    },
    Boolean(record?.bomsMap)
  );

  const { upsertJobsWithAttachments } = useJobActions();

  useEffect(() => {
    if (state?.id) {
      setId(state.id);
    }

    if (!state?.id && !id) {
      navigate(supportRequestListPath());
    }
  }, [state, id]);

  useEffect(() => {
    if (record && record.bomsMap) {
      reset(mapRequestToRequestJobs(record, attachments));
    }
  }, [record, attachments]);

  const {
    control,
    reset,
    formState: { errors },
    getValues,
    setValue,
    setError,
    handleSubmit,
  } = useForm<IRequestJobs>({
    resolver: yupResolver(requestJobsDetailsSchema()),
    mode: 'onSubmit',
    defaultValues: mapRequestToRequestJobs(record, attachments),
    reValidateMode: 'onSubmit',
    shouldFocusError: true,
  });

  const existingDisciplines = getExistingDisciplines(record?.bomsMap);

  const { fields, append, remove } = useFieldArray({
    name: 'jobs',
    control,
  });

  const { setFocusedElement } = useFocusFunctionality();

  useEffect(() => {
    setFocusedElement(`jobs.${fields.length - 1}.name`);
  }, [fields.length]);

  const onAddJob = (discipline: IDiscipline) => {
    append({
      id: null,
      name: `${record?.projectName} - ${discipline?.name}`,
      details: null,
      disciplineId: discipline?.id,
      disciplineName: discipline?.name,
      attachments: [],
    });
  };

  const onRemoveJob = async (discipline: IDiscipline) => {
    const index = fields.findIndex((job) => job.disciplineName === discipline.name);

    // clone jobs (without deleted one)
    const jobsArray = cloneArrayOfObjects(getValues('jobs'), index) as unknown as IJob[];

    remove(index);
    setValue('jobs', jobsArray, { shouldDirty: true });
  };

  const goToRequestJobsList = () => navigate(supportRequestJobListPath(record.id));

  const onSubmit = async (data: IRequestJobs) => {
    const { success } = await upsertJobsWithAttachments(
      mapUpsertRequestJobs(record.id, record.projectId, record.customerCompanyName, data.jobs)
    );

    if (!success) {
      return;
    }

    goToRequestJobsList();
  };

  return (
    <RequestJobsDetailsView
      editMode={editMode}
      request={record}
      requestJobs={fields}
      existingDisciplines={existingDisciplines}
      control={control}
      getValues={getValues}
      setValue={setValue}
      errors={errors}
      setError={setError}
      onAddJob={onAddJob}
      onRemoveJob={onRemoveJob}
      goToRequestJobsList={goToRequestJobsList}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isLoading || isFetching || isJobAttachmentsLoading}
      isError={isError || isJobAttachmentsError || !record}
      statusCode={statusCode || jobAttachmentsStatusCode}
      loaderMinHeight={VIEWPORT_HEIGHT}
    />
  );
};

export default RequestJobsDetails;
